import { useMemo, type ReactNode } from 'react';
import type {
  CalendarEvent,
  CalendarKindMap,
} from '../types.js';
import {
  addDays,
  buildWeek,
  formatHourLabel,
  instantFromZonedWallClock,
  isDateOnlyISO,
  isoDate,
  localDateKey,
  localTime,
  parseISODate,
  startOfWeek,
  todayPlain,
  weekRangeLabel,
} from '../utils/dateUtils.js';
import { List, PrimitiveButton } from '@viquard/doc-ui/components';
import { CalendarHeader } from '../components/CalendarHeader.js';
import { RailCard } from '../components/RailCard.js';
import { NowIndicator } from '../components/NowIndicator.js';
import { AllDayStrip } from '../components/AllDayStrip.js';
import { EventTimeBlock } from '../components/EventTimeBlock.js';
import { layoutDayEvents } from '../utils/layout.js';
import { applyEventDragCommit } from '../utils/dragApply.js';
import { useGridDragCreate } from '../hooks/useGridDragCreate.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { eventInstanceKey } from '../utils/identity.js';
import { formatEventCount, useCalendarMessages } from '../i18n.js';

export interface WeekViewProps {
  readonly cursor: string;
  readonly onCursorChange: (next: string) => void;
  readonly today: string;
  readonly events: readonly CalendarEvent[];
  readonly kindTheme: CalendarKindMap;
  readonly locale?: string | undefined;
  readonly weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  readonly viewerTz?: string | undefined;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectDay?: ((date: string) => void) | undefined;
  readonly onUpdateEvent?:
    | ((id: string, patch: { readonly startsAt: string; readonly endsAt: string | null }) => void)
    | undefined;
  readonly canUpdateEvent?: ((event: CalendarEvent) => boolean) | undefined;
  readonly onCreateEvent?:
    | ((draft: { readonly startsAt: string; readonly endsAt?: string }) => void)
    | undefined;
  readonly contextMenu?: ((event: CalendarEvent) => EventMenuActions) | undefined;
  /** Earliest hour shown. Default 8. */
  readonly startHour?: number | undefined;
  /** Latest hour shown (inclusive). Default 19. */
  readonly endHour?: number | undefined;
  readonly selectedEventKey?: string | null | undefined;
}

const HOUR_HEIGHT = 56;

export function WeekView(props: WeekViewProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    cursor,
    onCursorChange,
    today,
    events,
    kindTheme,
    locale,
    weekStartsOn = 0,
    viewerTz,
    onSelectEvent,
    onSelectDay,
    onUpdateEvent,
    canUpdateEvent,
    onCreateEvent,
    contextMenu,
    startHour = 8,
    endHour = 19,
    selectedEventKey,
  } = props;

  const cursorDate = useMemo(() => parseISODate(cursor), [cursor]);
  const weekStart = useMemo(() => startOfWeek(cursorDate, weekStartsOn), [cursorDate, weekStartsOn]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const days = useMemo(() => buildWeek(cursorDate, weekStartsOn), [cursorDate, weekStartsOn]);

  const hours = useMemo(() => {
    const list: number[] = [];
    for (let h = startHour; h <= endHour; h++) list.push(h);
    return list;
  }, [startHour, endHour]);

  // Bucket events by viewer-local day key. Then split each day into
  // all-day vs. timed; layout the timed ones with overlap detection.
  const perDay = useMemo(() => {
    const map = new Map<string, { allDay: CalendarEvent[]; timed: CalendarEvent[] }>();
    days.forEach((d) => map.set(isoDate(d), { allDay: [], timed: [] }));
    events.forEach((e) => {
      const key = localDateKey(e.startsAt, viewerTz);
      const bucket = map.get(key);
      if (bucket === undefined) return;
      if (e.allDay === true || isDateOnlyISO(e.startsAt)) bucket.allDay.push(e);
      else bucket.timed.push(e);
    });
    return map;
  }, [events, days, viewerTz]);

  // Pre-compute layout per day so we can render side-by-side overlap.
  const positionedPerDay = useMemo(() => {
    const map = new Map<string, ReturnType<typeof layoutDayEvents>>();
    perDay.forEach((bucket, key) => {
      map.set(key, layoutDayEvents(bucket.timed, viewerTz));
    });
    return map;
  }, [perDay, viewerTz]);

  // Aggregate counts for the rail
  const weekEvents: CalendarEvent[] = useMemo(() => {
    const out: CalendarEvent[] = [];
    perDay.forEach((bucket) => {
      out.push(...bucket.allDay, ...bucket.timed);
    });
    return out;
  }, [perDay]);

  const byKind = useMemo(() => {
    const m = new Map<string, number>();
    weekEvents.forEach((e) => m.set(e.kind, (m.get(e.kind) ?? 0) + 1));
    return m;
  }, [weekEvents]);

  // "Coming up" — next 6 events sorted by date+time.
  const upcoming = useMemo(() => {
    return weekEvents
      .slice()
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
      .slice(0, 6);
  }, [weekEvents]);

  const allAllDay = useMemo(() => {
    const out: CalendarEvent[] = [];
    perDay.forEach((b) => out.push(...b.allDay));
    return out;
  }, [perDay]);

  // Drag-to-create: a single hook services every column. The column id
  // is read off `data-day-key` so one hook covers all 7.
  const dragCreate = useGridDragCreate({
    hourHeight: HOUR_HEIGHT,
    startHour,
    snapMinutes: 30,
    disabled: onCreateEvent === undefined,
    onCommit: (c) => {
      const start = hourFractionToInstant(c.dayKey, c.startHourFraction, viewerTz);
      const end = hourFractionToInstant(c.dayKey, c.endHourFraction, viewerTz);
      onCreateEvent?.({
        startsAt: new Date(start).toISOString(),
        endsAt: new Date(end).toISOString(),
      });
    },
  });

  return (
    <div className="vq-cal__split vq-cal__split--week">
      <div className="vq-cal__panel vq-cal__panel--week">
        <CalendarHeader
          label={weekRangeLabel(weekStart, weekEnd, locale)}
          onPrev={() => { onCursorChange(isoDate(addDays(cursorDate, -7))); }}
          onNext={() => { onCursorChange(isoDate(addDays(cursorDate, 7))); }}
          onToday={() => { onCursorChange(isoDate(todayPlain(viewerTz))); }}
        />
        {allAllDay.length > 0 && (
          <AllDayStrip
            events={allAllDay}
            kindTheme={kindTheme}
            onSelectEvent={onSelectEvent}
            selectedEventKey={selectedEventKey}
          />
        )}
        <div className="vq-cal__week-scroll">
          <div
            className="vq-cal__week-grid"
            style={{ ['--vq-cal-hour-h' as string]: `${HOUR_HEIGHT}px` }}
          >
            <div className="vq-cal__week-headrow">
              <div className="vq-cal__week-gutter-head" />
              {days.map((d) => {
                const key = isoDate(d);
                const isToday = key === today;
                return (
                  <PrimitiveButton
                    key={key}
                    className={['vq-cal__week-dayhead', isToday ? 'is-today' : ''].filter(Boolean).join(' ')}
                    onClick={() => onSelectDay?.(key)}
                  >
                    <span className="vq-cal__week-dow">
                      {new Date(d.year, d.month - 1, d.day).toLocaleDateString(locale, {
                        weekday: 'short',
                      })}
                    </span>
                    <span className="vq-cal__week-day-num">{d.day}</span>
                  </PrimitiveButton>
                );
              })}
            </div>

            <div className="vq-cal__week-body">
              <div className="vq-cal__week-gutter">
                {hours.map((h) => (
                  <div key={h} className="vq-cal__week-hour-label">
                    {formatHourLabel(h, locale)}
                  </div>
                ))}
              </div>
              {days.map((d) => {
                const key = isoDate(d);
                const positioned = positionedPerDay.get(key) ?? [];
                const isToday = key === today;
                const ghost =
                  dragCreate.ghost !== null && dragCreate.ghost.dayKey === key
                    ? dragCreate.ghost
                    : null;
                return (
                  <div
                    key={key}
                    className="vq-cal__week-col"
                    data-day-key={key}
                    onPointerDown={dragCreate.bind.onPointerDown}
                  >
                    {hours.map((h) => (
                      <div key={h} className="vq-cal__week-hour" />
                    ))}
                    <NowIndicator
                      startHour={startHour}
                      endHour={endHour}
                      hourHeight={HOUR_HEIGHT}
                      active={isToday}
                      locale={locale}
                      viewerTz={viewerTz}
                    />
                    {positioned.map((p) => (
                      <EventTimeBlock
                        key={eventInstanceKey(p.event)}
                        positioned={p}
                        kindTheme={kindTheme}
                        startHour={startHour}
                        hourHeight={HOUR_HEIGHT}
                        timeLabel={localTime(p.event.startsAt, viewerTz)}
                        density="dense"
                        onClick={onSelectEvent}
                        dayKey={key}
                        onMoveCommit={
                          onUpdateEvent !== undefined && canUpdateEvent?.(p.event) !== false
                            ? (commit) => {
                                const patch = applyEventDragCommit(p.event, commit, key, {
                                  viewerTz,
                                });
                                if (patch !== null) onUpdateEvent(p.event.id, patch);
                              }
                            : undefined
                        }
                        onResizeCommit={
                          onUpdateEvent !== undefined && canUpdateEvent?.(p.event) !== false
                            ? (commit) => {
                                const patch = applyEventDragCommit(p.event, commit, key, {
                                  viewerTz,
                                });
                                if (patch !== null) onUpdateEvent(p.event.id, patch);
                              }
                            : undefined
                        }
                        selected={selectedEventKey === eventInstanceKey(p.event)}
                        contextMenu={contextMenu?.(p.event)}
                      />
                    ))}
                    {ghost !== null && (
                      <div
                        className="vq-cal__day-ghost"
                        style={{ top: ghost.top, height: ghost.height, left: 4, right: 4 }}
                        aria-hidden="true"
                      >
                        <span>{messages.newEvent}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <aside className="vq-cal__rail" aria-label={messages.weekSummaryAriaLabel}>
        <RailCard eyebrow={messages.thisWeek}>
          <div className="vq-cal__rail-headline">
            {formatEventCount(messages, weekEvents.length)}
          </div>
          <div className="vq-cal__rail-sub">{weekRangeLabel(weekStart, weekEnd, locale)}</div>
        </RailCard>
        <RailCard eyebrow={messages.agendaByKind}>
          {Object.entries(kindTheme)
            .filter(([k]) => k !== 'default')
            .map(([k, def]) => {
              const n = byKind.get(k) ?? 0;
              return (
                <div key={k} className="vq-cal__rail-row">
                  <span className="vq-cal__rail-row-label">
                    <span
                      className="vq-cal__rail-row-dot"
                      style={{ background: def.color }}
                      aria-hidden="true"
                    />
                    <span>{def.label}</span>
                  </span>
                  <span className={['vq-cal__rail-row-num', n === 0 ? 'is-mute' : ''].filter(Boolean).join(' ')}>
                    {n}
                  </span>
                </div>
              );
            })}
        </RailCard>
        {upcoming.length > 0 && (
          <RailCard eyebrow={messages.comingUp}>
            <List size="sm" aria-label={messages.upcomingListAriaLabel}>
              {upcoming.map((e) => {
                const key = localDateKey(e.startsAt, viewerTz);
                const t = localTime(e.startsAt, viewerTz);
                const dayDate = parseISODate(key);
                const dow = new Date(
                  dayDate.year,
                  dayDate.month - 1,
                  dayDate.day,
                ).toLocaleDateString(locale, { weekday: 'short' });
                return (
                  <List.Item
                    key={eventInstanceKey(e)}
                    leading={
                      <span className="vq-cal__rail-upcoming-date">
                        <span className="vq-cal__rail-upcoming-dow">{dow}</span>
                        <span className="vq-cal__rail-upcoming-num">{dayDate.day}</span>
                      </span>
                    }
                    title={e.title}
                    description={t ?? messages.composerDurationAllDay}
                    onClick={() => onSelectDay?.(key)}
                  />
                );
              })}
            </List>
          </RailCard>
        )}
      </aside>
    </div>
  );
}

function hourFractionToInstant(
  dayKey: string,
  fraction: number,
  viewerTz?: string,
): number {
  const d = parseISODate(dayKey);
  const wholeHour = Math.floor(fraction);
  const minute = Math.round((fraction - wholeHour) * 60);
  return instantFromZonedWallClock(d.year, d.month, d.day, wholeHour, minute, viewerTz);
}
